/**
 * LanguagesAdmin.tsx — Admin > Languages.
 *
 * A Prospera-only section (the corporate dashboard has no Languages page, so
 * there are no corp filters or columns to copy). Laid out like every Admin
 * list (Lead Sources is the reference — see components/admin/AdminList.tsx):
 *   - "Add Language" top right in the header;
 *   - a search box (name or hint) and Show deleted;
 *   - columns Name and Hint, Edit / Delete icons on the right (Restore on a
 *     deleted one), and a click on a live row opens its edit form.
 *
 * Delete is soft, as legacy: the language goes inactive (active = 0) and
 * Restore turns it back on. Only active languages are offered elsewhere.
 *   - 25 rows a page with the pagination footer (only the count while every
 *     language fits on the first page).
 *
 * Languages keep the order the API returns them in (their orderNumber) until
 * Name or Hint is clicked to sort by that column.
 *
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a row still opens, as a viewer with a single Close.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { FormField } from '@/components/ui/FormField';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { cn } from '@/lib/utils';
import * as adminSvc from '@/services/admin.service';
import { getErrorMessage } from '@/services/apiClient';
import { toast } from '@/store/toastStore';
import type { Language } from '@/services/admin.service';

// ─── Form state type ──────────────────────────────────────────────────────────

interface LanguageForm {
  name: string;
  hint: string;
}

const EMPTY_FORM: LanguageForm = {
  name: '',
  hint: '',
};

const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'Name', className: 'w-[30%]', sortable: true },
  { key: 'hint', header: 'Hint', sortable: true },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

/** No column is sorted to start: the list opens in the API's (orderNumber) order. */
const SORT_COMPARE: ListSortCompare<Language> = {
  name: (a, b) => compareText(a.name, b.name),
  hint: (a, b) => compareText(a.hint, b.hint),
};

// ─── Modal ────────────────────────────────────────────────────────────────────

interface LanguageModalProps {
  language?: Language | null;
  /** A new language's order: after every language the list has. */
  nextOrder?: number;
  onClose: () => void;
  onSaved: () => void;
}

function LanguageModal({ language, nextOrder = 0, onClose, onSaved }: LanguageModalProps) {
  const isEdit = language != null;
  // Locked: a viewer — fields read-only, Close only.
  const locked = useAdminPageLevel() === 'locked';

  const [form, setForm] = useState<LanguageForm>(() =>
    isEdit
      ? {
          name: language.name,
          hint: language.hint ?? '',
        }
      : EMPTY_FORM,
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof LanguageForm>(field: K, value: LanguageForm[K]) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || locked) return;

    const payload = {
      name: form.name.trim(),
      hint: form.hint.trim() || null,
    };

    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await adminSvc.updateLanguage(language.languageId, payload);
      } else {
        await adminSvc.createLanguage({ ...payload, orderNumber: nextOrder });
      }
      onSaved();
      onClose();
    } catch {
      setError('Failed to save language. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={locked ? 'Language' : isEdit ? 'Edit Language' : 'Add Language'}
      size="md"
      footer={
        locked ? (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving || !form.name.trim()}
              loading={saving}
            >
              {isEdit ? 'Save Changes' : 'Add Language'}
            </Button>
          </>
        )
      }
    >
      <AdminLockedFieldset>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}

          <FormField label="Name" required={!locked}>
            <Input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. English"
              required
              autoFocus
            />
          </FormField>

          <FormField label="Hint">
            <Textarea
              value={form.hint}
              onChange={(e) => set('hint', e.target.value)}
              placeholder="Optional hint text"
              rows={3}
            />
          </FormField>
        </form>
      </AdminLockedFieldset>
    </Modal>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function LanguagesAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [items, setItems] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState<Language | null>(null);
  const [deleteItem, setDeleteItem] = useState<Language | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminSvc.listLanguages();
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDelete() {
    if (!deleteItem) return;
    try {
      await adminSvc.updateLanguage(deleteItem.languageId, { active: false });
      toast.success(`"${deleteItem.name}" deleted`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Delete failed'));
    }
    setDeleteItem(null);
    void load();
  }

  async function handleRestore(language: Language) {
    try {
      await adminSvc.updateLanguage(language.languageId, { active: true });
      toast.success(`${language.name} restored`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Restore failed'));
    }
    void load();
  }

  const query = search.trim().toLowerCase();
  const rows = useMemo(() => {
    const listed = showDeleted ? items : items.filter((l) => l.active);
    return query === ''
      ? listed
      : listed.filter((l) => l.name.toLowerCase().includes(query) || (l.hint ?? '').toLowerCase().includes(query));
  }, [items, query, showDeleted]);

  const sorting = useListSort(rows, { compare: SORT_COMPARE });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  return (
    <AdminListPage
      title="Languages"
      subtitle="Manage available languages."
      actions={
        <Button onClick={() => setShowCreate(true)} leftIcon={<Plus className="h-4 w-4" />}>
          Add Language
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name or hint…"
          searchLabel="Search languages"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="languages-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading languages…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title="No languages found"
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add your first language to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Languages" sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((row) => {
              const deleted = !row.active;
              return (
                <tr
                  key={row.languageId}
                  data-language-id={row.languageId}
                  className={adminRowClass({ clickable: !deleted, deleted })}
                  // A deleted language is restored, not edited.
                  onClick={deleted ? undefined : () => setEditItem(row)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName nameTestId="language-name" deleted={deleted}>
                      {row.name}
                    </AdminRowName>
                  </td>
                  <td className={cn(ADMIN_TD_CLASS, 'truncate')}>{row.hint || '—'}</td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${row.name}`} onClick={() => void handleRestore(row)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${row.name}`} onClick={() => setEditItem(row)} />
                            <AdminRowAction kind="delete" label={`Delete ${row.name}`} onClick={() => setDeleteItem(row)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['language', 'languages']} />
        </>
      )}

      {showCreate && (
        <LanguageModal
          nextOrder={items.reduce((max, l) => Math.max(max, l.orderNumber), 0) + 1}
          onClose={() => setShowCreate(false)}
          onSaved={load}
        />
      )}

      {editItem && (
        <LanguageModal
          language={editItem}
          onClose={() => setEditItem(null)}
          onSaved={load}
        />
      )}

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Delete Language"
        message={
          deleteItem
            ? `Delete "${deleteItem.name}"? It will no longer be offered. You can restore it from Show deleted.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteItem(null)}
      />
    </AdminListPage>
  );
}
